/**
 * @file featureFlags.ts
 * @description Centralized feature flag management using PostHog,  and environment variables
 */

import { usePostHog } from 'posthog-js/react';
import { useSession } from 'next-auth/react';

/**
 * Hook for checking PostHog feature flags
 */
export function useFeatureFlags() {
  const posthog = usePostHog();
  const { data: session } = useSession();

  const isFeatureEnabled = (flagKey: string): boolean => {
    if (!posthog || !session?.user?.email) return false;
    
    // PostHog feature flag check - ensure we return a boolean
    return Boolean(posthog.isFeatureEnabled(flagKey));
  };

  return {
    // NodeBB Integration flags
    nodeBBIntegration: isFeatureEnabled('nodebb-integration'),
    nodeBBAutoLogin: isFeatureEnabled('nodebb-auto-login'),
    
    // Generic feature flag checker
    isEnabled: isFeatureEnabled
  };
}

/**
 * Environment-based feature flags (fallback)
 */
export const envFeatureFlags = {
  nodeBBIntegration: process.env.NEXT_PUBLIC_ENABLE_NODEBB === 'true',
  nodeBBAutoLogin: process.env.NEXT_PUBLIC_NODEBB_AUTO_LOGIN === 'true',
};

/**
 * Combined feature flag checker with fallbacks
 */
export function useFeatureFlagsWithFallback() {
  const posthogFlags = useFeatureFlags();
  
  return {
    nodeBBIntegration: posthogFlags.nodeBBIntegration || envFeatureFlags.nodeBBIntegration,
    nodeBBAutoLogin: posthogFlags.nodeBBAutoLogin || envFeatureFlags.nodeBBAutoLogin,
  };
}
